import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { subscribePolicy, evaluateGoal } from "../../lib/platformPolicy";
import { PersonalDetailsSection } from "./PersonalDetailsSection";
import { ProfileBioSection } from "./ProfileBioSection";
import { FundingNeedSection } from "./FundingNeedSection";
import { PhotosSection } from "./PhotosSection";
import { VideoSection, VisibilitySection } from "./MediaAndVisibility";
import { AttestationsSection } from "./AttestationsSection";
import { BuilderActions, Checklist } from "./BuilderActions";
import { ReviewLockedPanel, PendingChangesNotice, ApprovedNote, PausedNote } from "./BuilderStates";
import { ExtensionPanel, ClosedPanel } from "./LifecyclePanels";
import { PayoutInline } from "./PayoutInline";
import { SupporterThanks } from "./SupporterThanks";
import { BuilderStepTrack } from "./BuilderStepTrack";

const TEXT_KEYS = ["full_name", "school", "program", "grad_year", "phone", "location", "headline", "bio", "title", "category", "deadline", "story"];
const PENDING_KEYS = ["title", "story", "category", "school", "program", "goal_cents", "cover_file_id", "request_doc_file_id",
  "full_name", "grad_year", "location", "headline", "bio", "deadline", "photo_file_ids", "visibility"];
const EMPTY_ATT = { eligibility: false, enrollment_consent: false, student_cert: false };

/** Live values merged with the student's latest (pending or returned) edits. */
const effectiveOf = (draft) => {
  const pc = draft.pending_changes || {};
  const overlay = Object.fromEntries(PENDING_KEYS.filter((k) => k in pc).map((k) => [k, pc[k]]));
  return { ...draft, ...overlay };
};

const formOf = (draft) => {
  const eff = effectiveOf(draft);
  const f = Object.fromEntries(TEXT_KEYS.map((k) => [k, (k === "phone" ? draft.phone : eff[k]) || ""]));
  f.goal = eff.goal_cents ? String(eff.goal_cents / 100) : "";
  f.visibility = eff.visibility || "public";
  f.attestations = { ...EMPTY_ATT, ...(draft.attestations || {}) };
  return f;
};

const bodyOf = (form) => {
  const body = Object.fromEntries(TEXT_KEYS.map((k) => [k, form[k].trim()]));
  body.visibility = form.visibility;
  body.attestations = form.attestations;
  const g = Number(form.goal);
  if (form.goal !== "" && g >= 1) body.goal_cents = Math.round(g * 100);
  return body;
};

/** Server checklist, refreshed with what's typed but not yet saved. */
const liveChecklist = (checklist, form) => {
  if (!checklist) return null;
  const items = { ...checklist.items };
  for (const k of ["full_name", "school", "bio", "title", "story", "category"]) {
    if (items[k]) items[k] = { ...items[k], done: Boolean(form[k].trim()) };
  }
  if (items.attestations) items.attestations = { ...items.attestations, done: Object.values(form.attestations).every(Boolean) };
  if (items.request_doc) items.request_doc = { ...items.request_doc, required: Number(form.goal) > 500 };
  return { ...checklist, items };
};

export const CampaignBuilder = ({ draftState, onSubmitted }) => {
  const { user } = useAuth();
  const { draft, loading, saving, patch, refresh, discardPendingChanges, requestExtension, startNewCampaign } = draftState;
  const [form, setForm] = useState(null);
  const baseline = useRef("");
  const [, setPolicyTick] = useState(0);
  useEffect(() => subscribePolicy(() => setPolicyTick((n) => n + 1)), []);

  useEffect(() => {
    if (!draft) return;
    const next = formOf(draft);
    const serialized = JSON.stringify(next);
    const wasDirty = form && JSON.stringify(form) !== baseline.current;
    baseline.current = serialized;
    if (!wasDirty) setForm(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const dirty = useMemo(() => !!form && JSON.stringify(form) !== baseline.current, [form]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // While in review, quietly re-read so the progress track moves as the reviewer works.
  const inReview = draft?.status === "pending_review";
  useEffect(() => {
    if (!inReview) return undefined;
    const id = setInterval(() => refresh({ silent: true }), 45000);
    return () => clearInterval(id);
  }, [inReview, refresh]);

  if (loading || !draft || !form) {
    return (
      <div className="flex items-center gap-2 rounded-2xl border-2 border-ink/10 bg-bone p-8 text-olive" data-testid="builder-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading your campaign…
      </div>
    );
  }

  const eff = effectiveOf(draft);
  const isLive = draft.status === "live" || draft.status === "paused";
  const goalEval = evaluateGoal(form.goal);
  const checklist = liveChecklist(draft.checklist, form);

  const save = async (extra = {}) => {
    try {
      const res = await patch({ ...bodyOf(form), ...extra });
      if (res) {
        const synced = formOf(res);
        baseline.current = JSON.stringify(synced);
        setForm(synced);
      }
      return res;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail[0]?.msg : (detail || "Save failed."));
      return null;
    }
  };

  const onSave = async () => {
    const res = await save();
    if (res) toast.success(isLive ? "Edits saved for review." : "Draft saved.");
  };

  const onSubmit = async () => {
    const att = form.attestations;
    if (!att.eligibility) { toast.error("First, confirm you're the enrolled student creating this campaign."); return; }
    if (!att.enrollment_consent) { toast.error("Please agree to enrollment verification before you submit."); return; }
    if (!att.student_cert) { toast.error("One more thing: please review and check the student certification."); return; }
    if (goalEval.exceedsCap) {
      toast.error(`During the ${goalEval.activePhase} phase, campaigns can request up to $${goalEval.cap}.`, { description: "Lower your goal amount to submit." });
      return;
    }
    const res = await save({ status: "pending_review" });
    if (res) {
      toast.success("Submitted for review.", { description: "One reviewer will look at everything at once." });
      document.getElementById("draft")?.scrollIntoView({ behavior: "smooth", block: "start" });
      onSubmitted?.();
    }
  };

  const onDiscard = async () => {
    await discardPendingChanges();
    toast("Edits discarded.", { description: "Your live page is exactly as it was." });
  };

  const saveAssets = async (fields) => {
    try { await patch(fields); } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
  };

  return (
    <section id="draft" data-testid="campaign-builder" className="space-y-6">
      {draft.status !== "pending_review" && (
      <div>
        <h2 className="font-serif text-3xl leading-tight text-ink">
          {draft.status === "paused" ? "Your page (paused)." : isLive ? "Your page." : draft.status === "closed" ? "Campaign closed." : "Build your page."}
        </h2>
        {!isLive && draft.status !== "closed" && (
          <>
            <p className="mt-2 max-w-xl text-base leading-relaxed text-olive">
              Everything in one place: who you are, what you need, where grants land. Save as you go, then submit once. One reviewer verifies your enrollment and approves your page together.
            </p>
            <BuilderStepTrack checklist={checklist} form={form} />
          </>
        )}
      </div>
      )}

      {draft.status === "closed" ? (
        <>
          <ClosedPanel draft={draft} onStartNew={startNewCampaign} />
          <PayoutInline />
          <SupporterThanks campaignId={draft.id} />
        </>
      ) : draft.status === "pending_review" ? (
        <ReviewLockedPanel draft={draft} payoutState={draft.payout_state} />
      ) : (
        <>
          {draft.status === "paused" && <PausedNote />}
          {isLive && (draft.pending_changes?.status ? <PendingChangesNotice pending={draft.pending_changes} onDiscard={onDiscard} /> : draft.status === "live" && <ApprovedNote />)}
          {isLive && <ExtensionPanel draft={draft} onRequest={requestExtension} />}
          {isLive && <SupporterThanks campaignId={draft.id} />}

          <PersonalDetailsSection form={form} set={set} email={user?.email} />
          <ProfileBioSection form={form} set={set} campaignId={draft.id} coverFileId={eff.cover_file_id} onCoverChange={() => refresh()} />
          <FundingNeedSection
            form={form}
            set={set}
            campaignId={draft.id}
            goalCap={goalEval.cap}
            durationDays={draft.duration_days}
            requestDocId={eff.request_doc_file_id}
            onDocChange={() => refresh()}
          />
          <PhotosSection photoIds={eff.photo_file_ids || []} onChange={(ids) => saveAssets({ photo_file_ids: ids })} />
          <VideoSection campaignId={draft.id} />
          <VisibilitySection value={form.visibility} onChange={(v) => set("visibility")(v)} />
          {!isLive && <AttestationsSection value={form.attestations} onChange={(v) => set("attestations")(v)} />}
          {!isLive && <Checklist checklist={checklist} />}
          <BuilderActions
            campaignId={draft.id}
            dirty={dirty}
            saving={saving}
            isLive={isLive}
            onSave={onSave}
            onSubmit={onSubmit}
            resubmit={draft.status === "rejected"}
          />
        </>
      )}
    </section>
  );
};
